import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import {
  getCachedHighScores,
  getStoredHighScores,
  ensureHighScoresLoaded,
  clearHighScoresCache,
} from '@/lib/highScoresCache';
import InitialsEntry from './InitialsEntry';
import LoadingBar from './LoadingBar';
import { KaijuId } from '@/lib/gameData';

interface HighScoresScreenProps {
  stats: {
    score: number;
    correct: number;
    wrong: number;
    expired: number;
    total: number;
    livesSaved: number;
    preventableDeteriorations: number;
    preventableDeaths: number;
    unnecessarySteps: number;
  };
  accuracy: number;
  kaijuId: KaijuId;
  onMainMenu: () => void;
}

interface HighScoreRow {
  id: string;
  initials: string;
  score: number;
  accuracy: number;
  lives_saved: number;
  kaiju_id: string;
  created_at: string;
}

const HighScoresScreen = ({ stats, accuracy, kaijuId, onMainMenu, viewOnly = false }: HighScoresScreenProps & { viewOnly?: boolean }) => {
  const initialSync = viewOnly ? (getCachedHighScores() ?? getStoredHighScores()) : null;
  const [phase, setPhase] = useState<'loading' | 'entry' | 'leaderboard'>(
    viewOnly ? (initialSync ? 'leaderboard' : 'loading') : 'loading'
  );
  const [weeklyScores, setWeeklyScores] = useState<HighScoreRow[]>(initialSync?.weekly ?? []);
  const [allTimeScores, setAllTimeScores] = useState<HighScoreRow[]>(initialSync?.allTime ?? []);
  const [playerInitials, setPlayerInitials] = useState('');
  const dataReadyRef = useRef(false);
  const nextPhaseRef = useRef<'entry' | 'leaderboard'>('leaderboard');

  const fetchScores = async () => {
    const data = await ensureHighScoresLoaded();
    setWeeklyScores(data.weekly);
    setAllTimeScores(data.allTime);
    return data;
  };

  useEffect(() => {
    (async () => {
      if (viewOnly) {
        // Background refresh; sync data already rendered if available
        const data = await ensureHighScoresLoaded();
        setWeeklyScores(data.weekly);
        setAllTimeScores(data.allTime);
        setPhase('leaderboard');
        return;
      }
      const { weekly, allTime } = await fetchScores();
      const qualifiesWeekly = weekly.length < 10 || stats.score > (weekly[weekly.length - 1]?.score ?? 0);
      const qualifiesAllTime = allTime.length < 10 || stats.score > (allTime[allTime.length - 1]?.score ?? 0);
      nextPhaseRef.current = qualifiesWeekly || qualifiesAllTime ? 'entry' : 'leaderboard';
      dataReadyRef.current = true;
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoadingComplete = useCallback(() => {
    if (dataReadyRef.current) {
      setPhase(nextPhaseRef.current);
    } else {
      const id = setInterval(() => {
        if (dataReadyRef.current) {
          clearInterval(id);
          setPhase(nextPhaseRef.current);
        }
      }, 50);
    }
  }, []);

  const submittingRef = useRef(false);

  const handleSubmit = async (initials: string) => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setPlayerInitials(initials);
    await supabase.from('high_scores').insert({
      initials,
      score: stats.score,
      accuracy,
      lives_saved: stats.livesSaved,
      kaiju_id: kaijuId,
    });
    clearHighScoresCache();
    await fetchScores();
    setPhase('leaderboard');
  };


  if (phase === 'loading') {
    if (viewOnly) {
      return (
        <div className="relative h-full overflow-y-auto flex flex-col items-center p-4 pt-6">
          <div className="w-full max-w-2xl text-center space-y-6 animate-pulse">
            <div className="h-8 w-48 mx-auto rounded bg-muted" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-64 rounded-xl bg-card border border-border" />
              <div className="h-64 rounded-xl bg-card border border-border" />
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="relative w-full h-full min-h-[320px]">
        <LoadingBar duration={1400} label="CALCULATING" onComplete={handleLoadingComplete} />
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-y-auto flex flex-col items-center p-4 pt-6">
      <div className="w-full max-w-2xl text-center space-y-6 animate-fade-in">
        {phase === 'entry' && (
          <InitialsEntry score={stats.score} onSubmit={handleSubmit} onSkip={() => setPhase('leaderboard')} />
        )}

        {phase === 'leaderboard' && (
          <>
            <h2 className="text-2xl md:text-3xl font-display font-bold text-primary text-glow-cyan uppercase tracking-wider">
              High Scores
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <ScoreTable title="This Week" scores={weeklyScores} highlight={playerInitials} />
              <ScoreTable title="All Time" scores={allTimeScores} highlight={playerInitials} />
            </div>

            <button
              onClick={onMainMenu}
              className="w-full py-4 rounded-lg bg-secondary text-foreground font-display text-xl font-bold uppercase tracking-wider hover:bg-secondary/80 hover:scale-[1.02] transition-all"
            >
              Main Menu
            </button>
          </>
        )}
      </div>
    </div>
  );
};

function ScoreTable({
  title,
  scores,
  highlight,
}: {
  title: string;
  scores: HighScoreRow[];
  highlight: string;
}) {
  return (
    <div className="bg-card border border-border rounded-xl p-4 space-y-3">
      <h3 className="font-display font-bold text-lg text-accent uppercase tracking-wider">
        {title}
      </h3>
      <div className="space-y-1">
        <div className="grid grid-cols-[2rem_3.5rem_1fr] gap-2 text-xs font-mono-game text-muted-foreground border-b border-border pb-1">
          <span>#</span>
          <span>INI</span>
          <span className="text-right">SCORE</span>
        </div>
        {scores.length === 0 && (
          <p className="text-sm font-mono-game text-muted-foreground py-4">No scores yet</p>
        )}
        {scores.map((s, i) => {
          const isHighlight = s.initials === highlight;
          return (
            <div
              key={s.id}
              className={`grid grid-cols-[2rem_3.5rem_1fr] gap-2 text-sm font-mono-game py-1 ${
                isHighlight ? 'text-primary font-bold' : 'text-foreground'
              }`}
            >
              <span className={i < 3 ? 'text-accent font-bold' : 'text-muted-foreground'}>
                {i + 1}.
              </span>
              <span>{s.initials}</span>
              <span className="text-right">{s.score}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default HighScoresScreen;
